import { useEffect, useRef, useState } from "react";

/** A form control whose value the dialog reads. */
export type DialogFieldElement =
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/** A field's ref: getFieldRef hands it out, the control attaches to it. */
export interface DialogFieldRef {
  current: DialogFieldElement | null;
}

/** The form's values by field name, as the controls hold them. */
export type DialogFormData = Record<string, string>;

export default function useDialogForm(
  open: boolean,
  initialValues: Record<string, unknown> = {},
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset the submitting flag as the dialog opens, during render rather than
  // in an effect (https://react.dev/learn/you-might-not-need-an-effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setIsSubmitting(false);
  }
  const formRef = useRef<HTMLFormElement>(null);

  // Create refs for each field dynamically
  const fieldRefs = useRef<Record<string, DialogFieldRef>>({});

  // Initialize field refs
  useEffect(() => {
    if (open && initialValues) {
      Object.keys(initialValues).forEach((key) => {
        if (!fieldRefs.current[key]) {
          fieldRefs.current[key] = { current: null };
        }
      });
    }
  }, [open, initialValues]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Set initial values - use requestAnimationFrame to ensure refs are attached
      requestAnimationFrame(() => {
        Object.entries(initialValues).forEach(([key, value]) => {
          const ref = fieldRefs.current[key];
          if (ref?.current) {
            // The DOM would coerce a non-string the same way.
            ref.current.value = String(value ?? "");
          }
        });
      });
    }
  }, [open, initialValues]);

  const getFieldRef = (fieldName: string): DialogFieldRef => {
    if (!fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName] = { current: null };
    }
    return fieldRefs.current[fieldName];
  };

  const getFieldValue = (fieldName: string): string => {
    const ref = fieldRefs.current[fieldName];
    return ref?.current?.value ?? "";
  };

  const getFormData = () => {
    const data: DialogFormData = {};
    Object.keys(fieldRefs.current).forEach((key) => {
      data[key] = getFieldValue(key);
    });
    return data;
  };

  const handleSubmit = async (
    onSubmit: (formData: DialogFormData) => unknown,
  ) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const formData = getFormData();
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    setIsSubmitting,
    getFieldRef,
    getFieldValue,
    getFormData,
    handleSubmit,
    formRef,
  };
}
